<template>
  <div class="report-page">
    <!-- 顶部祝贺区域 -->
    <div class="congrats-section">
      <div class="congrats-inner">
        <div class="congrats-icon" :class="{ 'aborted': status === 2 }">
          <svg v-if="status === 2" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" class="warning-svg">
            <circle cx="32" cy="32" r="28" fill="#FEF3C7" stroke="#F59E0B" stroke-width="2"/>
            <path d="M32 20v16M32 44h.01" stroke="#F59E0B" stroke-width="4" stroke-linecap="round"/>
          </svg>
          <svg v-else viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" class="star-svg">
            <path d="M32 4L39.5 22.5L59 24.5L44 38L48.5 57L32 47.5L15.5 57L20 38L5 24.5L24.5 22.5L32 4Z" fill="#60A5FA" stroke="#3B82F6" stroke-width="2" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="congrats-text">
          <h2 v-if="status === 2">{{ $t('report.abortedTitle') }}</h2>
          <h2 v-else>{{ $t('report.congrats') }}</h2>
          <p v-if="status === 2">{{ $t('report.abortedSubtitle') }}</p>
          <p v-else>{{ $t('report.subtitle') }}</p>
        </div>
      </div>
      <div class="action-buttons">
        <button class="lang-btn" @click="toggleLang">{{ switchLangLabel }}</button>
        <button class="download-btn" @click="downloadReport">
          <svg class="download-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <polyline points="7,10 12,15 17,10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ $t('report.download') }}
        </button>
        <button class="back-home-btn" @click="$router.push('/')">
          <svg class="back-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 21V12h6v9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ $t('report.backHome') }}
        </button>
      </div>
    </div>

    <!-- 证书/成绩报告 -->
    <div class="certificate-wrapper" ref="certificate">
      <div class="certificate">
        <!-- 装饰边框 -->
        <div class="cert-border">
          <div class="cert-inner">
            <!-- Logo -->
            <div class="cert-logo">
              <img src="/logo.png" alt="logo" class="cert-logo-img" />
            </div>

            <!-- 标题 -->
            <h1 class="cert-title">{{ $t('report.certTitle') }}</h1>
            <p class="cert-title-en">Chinese Proficiency Test</p>

            <div class="cert-grade-badge">
              <span class="grade-letter" :class="gradeColorClass(reportData.grade)">{{ reportData.grade }}</span>
              <span class="grade-label">{{ $t('report.gradeLabel') }}</span>
            </div>
            <p class="cert-level-en">Chinese Proficiency Assessment Score Report</p>

            <!-- 个人信息区域 -->
            <div class="cert-info">
              <div class="cert-info-left">
                <div class="info-row">
                  <span class="info-label">{{ $t('report.name') }}</span>
                  <span class="info-value info-line">{{ reportData.name }}</span>
                </div>
                <div class="info-row-half">
                  <div class="info-item">
                    <span class="info-label">{{ $t('report.gender') }}</span>
                    <span class="info-value info-line">{{ reportData.gender }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">{{ $t('report.nationality') }}</span>
                    <span class="info-value info-line">{{ reportData.nationality }}</span>
                  </div>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('report.examDate') }}</span>
                  <span class="info-value">
                    <span class="info-line-short">{{ reportData.year }}</span>{{ $t('report.year') }}
                    <span class="info-line-short">{{ reportData.month }}</span>{{ $t('report.month') }}
                    <span class="info-line-short">{{ reportData.day }}</span>{{ $t('report.day') }}
                  </span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('report.examNo') }}</span>
                  <span class="info-value info-line">{{ reportData.examNo }}</span>
                </div>
              </div>
              <div class="cert-photo">
                <img :src="reportData.photo || '/avatar-default.png'" alt="photo" />
              </div>
            </div>

            <!-- 成绩表格 + 雷达图 -->
            <div class="cert-scores">
              <table class="score-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>{{ $t('report.fullScore') }}</th>
                    <th>{{ $t('report.yourScore') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ $t('report.listening') }}<br/><span class="td-en">Listening</span></td>
                    <td>{{ reportData.scores.listening.full }}</td>
                    <td>{{ reportData.scores.listening.score }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t('report.oral') }}<br/><span class="td-en">Oral</span></td>
                    <td>{{ reportData.scores.oral.full }}</td>
                    <td>{{ reportData.scores.oral.score }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t('report.reading') }}<br/><span class="td-en">Reading</span></td>
                    <td>{{ reportData.scores.reading.full }}</td>
                    <td>{{ reportData.scores.reading.score }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t('report.writing') }}<br/><span class="td-en">Writing</span></td>
                    <td>{{ reportData.scores.writing.full }}</td>
                    <td>{{ reportData.scores.writing.score }}</td>
                  </tr>
                  <tr class="total-row">
                    <td>{{ $t('report.totalScore') }}<br/><span class="td-en">Total Score</span></td>
                    <td>{{ reportData.scores.total.full }}</td>
                    <td>{{ reportData.scores.total.score }}<br/><span class="td-grade" :class="gradeColorClass(reportData.grade)">{{ reportData.grade }}</span></td>
                  </tr>
                </tbody>
              </table>

              <!-- 雷达图 -->
              <div class="radar-chart">
                <canvas ref="radarCanvas" width="240" height="240"></canvas>
              </div>
            </div>

            <!-- 底部：印章 + 签名 -->
            <div class="cert-footer">
              <div class="cert-footer-left">
                <div class="cert-stamp-placeholder"></div>
                <div class="cert-sign">
                  <span class="sign-label">{{ $t('report.director') }}</span>
                  <span class="sign-line"></span>
                </div>
              </div>
              <div class="cert-footer-right">
                <div class="cert-org">{{ $t('report.org') }}</div>
                <div class="cert-org-en">Center for Language Education and Cooperation</div>
<!--                <div class="cert-seal"></div>-->
              </div>
            </div>

            <div class="cert-location">
              <span>{{ $t('report.location') }}</span>
              <span class="cert-location-en">Shanghai · China</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'ReportPage',
  data() {
    return {
      status: 0, // 0-进行中，1-已完成，2-已终止
      reportData: {
        name: '',
        gender: '',
        nationality: '',
        year: new Date().getFullYear(),
        month: String(new Date().getMonth() + 1).padStart(2, '0'),
        day: String(new Date().getDate()).padStart(2, '0'),
        examNo: '',
        photo: '/avatar-default.png',
        grade: '--',
        scores: {
          listening: { full: 100, score: 0 },
          oral: { full: 100, score: 0 },
          reading: { full: 100, score: 0 },
          writing: { full: 100, score: 0 },
          total: { full: 100, score: 0 }
        },
        radarScores: {
          listening: 0,
          oral: 0,
          reading: 0,
          grammar: 0,
          vocabulary: 0,
          speaking: 0,
          writing: 0
        }
      },
    }
  },
  mounted() {
    this.loadReport()
    this._onBeforePrint = this.onBeforePrint.bind(this)
    window.addEventListener('beforeprint', this._onBeforePrint)
  },
  beforeDestroy() {
    if (this._onBeforePrint) {
      window.removeEventListener('beforeprint', this._onBeforePrint)
    }
  },
  computed: {
    switchLangLabel() {
      return this.$i18n.locale === 'zh' ? 'EN' : '中'
    }
  },
  methods: {
    toggleLang() {
      this.$i18n.locale = this.$i18n.locale === 'zh' ? 'en' : 'zh'
      localStorage.setItem('lang', this.$i18n.locale)
    },
    async loadReport() {
      var sessionId = this.$route.query.sessionId

      // 获取用户个人信息（姓名、性别、国籍）
      try {
        var profileRes = await api.get('/api/user/profile')
        if (profileRes.code === 200 && profileRes.data) {
          var u = profileRes.data
          var familyName = u.familyName || ''
          var givenName = u.givenName || ''
          this.reportData.name = (familyName + givenName).trim() || u.username || ''
          var genderMap = { 1: '男', 2: '女' }
          this.reportData.gender = genderMap[u.gender] || ''
          this.reportData.nationality = u.country || ''
          if (u.avatar && String(u.avatar).trim()) {
            this.reportData.photo = String(u.avatar).trim()
          }
        }
      } catch (e) {
        console.error('获取用户信息失败:', e)
      }

      if (!sessionId) {
        // 没有 sessionId，使用默认数据
        this.$nextTick(() => { this.drawRadar() })
        return
      }

      try {
        var res = await api.get('/api/assessment/result/' + sessionId)
        if (res.code === 200 && res.data) {
          var data = res.data
          this.status = data.status || 0
          var rounds = data.roundResults || []

          // 按阶段分组计算各项平均分（满分100）
          var phase1 = rounds.filter(r => r.phase === 1)
          var phase2 = rounds.filter(r => r.phase === 2)
          var phase3 = rounds.filter(r => r.phase === 3)
          var phase4 = rounds.filter(r => r.phase === 4)

          var listeningScore = this.calcAvg(phase1, 'suggestedScore')
          var oralScore = this.calcAvg(phase2, 'suggestedScore')
          var readingScore = this.calcAvg(phase3, 'suggestedScore')
          var writingScore = this.calcAvg(phase4, 'llmScore') || this.calcAvg(phase4, 'suggestedScore')

          this.reportData.scores.listening.score = listeningScore
          this.reportData.scores.oral.score = oralScore
          this.reportData.scores.reading.score = readingScore
          this.reportData.scores.writing.score = writingScore

          // 总分 = 所有题目的平均分（满分100）
          var allRounds = rounds.filter(function(r) { return r.suggestedScore != null || r.llmScore != null })
          var totalAvg = 0
          if (allRounds.length > 0) {
            var totalSum = allRounds.reduce(function(acc, r) {
              var s = r.llmScore != null ? Number(r.llmScore) : Number(r.suggestedScore)
              return acc + s
            }, 0)
            totalAvg = Math.round(totalSum / allRounds.length)
          }
          this.reportData.scores.total.score = totalAvg
          this.reportData.scores.total.full = 100
          this.reportData.grade = data.grade || '--'

          // 雷达图分数直接复用各阶段平均分（0-100范围）
          var pronAccuracy = this.calcAvg([].concat(phase1, phase2, phase3), 'pronAccuracy')
          var pronFluency = this.calcAvg([].concat(phase1, phase2, phase3), 'pronFluency')
          this.reportData.radarScores.listening = listeningScore
          this.reportData.radarScores.oral = oralScore
          this.reportData.radarScores.reading = readingScore
          this.reportData.radarScores.writing = writingScore
          this.reportData.radarScores.speaking = pronAccuracy
          this.reportData.radarScores.grammar = pronFluency
          this.reportData.radarScores.vocabulary = Math.round((listeningScore + readingScore) / 2)

          // 评测编号
          this.reportData.examNo = sessionId

          // 考试日期：使用本次测评会话的开始时间（后端 createTime）
          var examParts = this.parseExamDateParts(data.createTime)
          if (examParts) {
            this.reportData.year = examParts.year
            this.reportData.month = examParts.month
            this.reportData.day = examParts.day
          }
        }
      } catch (e) {
        console.error('加载报告失败:', e)
      }

      this.$nextTick(() => { this.drawRadar() })
    },

    /**
     * 解析后端返回的考试开始时间（ISO 字符串或 Jackson 数组格式）
     */
    parseExamDateParts(createTime) {
      if (createTime == null) return null
      if (Array.isArray(createTime)) {
        var y = createTime[0]
        var mo = createTime[1]
        var d = createTime[2]
        if (y == null || mo == null || d == null) return null
        return {
          year: y,
          month: String(mo).padStart(2, '0'),
          day: String(d).padStart(2, '0')
        }
      }
      var s = String(createTime)
      var m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (m) {
        return { year: Number(m[1]), month: m[2], day: m[3] }
      }
      var dt = new Date(s)
      if (!isNaN(dt.getTime())) {
        return {
          year: dt.getFullYear(),
          month: String(dt.getMonth() + 1).padStart(2, '0'),
          day: String(dt.getDate()).padStart(2, '0')
        }
      }
      return null
    },

    calcSum(items, field) {
      var scored = items.filter(r => r[field] != null)
      if (scored.length === 0) return 0
      return Math.round(scored.reduce((acc, r) => acc + Number(r[field]), 0))
    },

    calcAvg(items, field) {
      var scored = items.filter(r => r[field] != null)
      if (scored.length === 0) return 0
      var sum = scored.reduce((acc, r) => acc + Number(r[field]), 0)
      return Math.round(sum / scored.length)
    },

    downloadReport() {
      this.$nextTick(() => {
        this.drawRadar()
        window.print()
      })
    },

    onBeforePrint() {
      this.$nextTick(() => {
        this.drawRadar()
      })
    },

    gradeColorClass(grade) {
      const map = {
        'C2': 'grade-c2',
        'C1': 'grade-c1',
        'B2': 'grade-b2',
        'B1': 'grade-b1',
        'A2': 'grade-a2',
        'A1': 'grade-a1'
      }
      return map[grade] || 'grade-default'
    },

    drawRadar() {
      var canvas = this.$refs.radarCanvas
      if (!canvas) return
      var ctx = canvas.getContext('2d')
      var w = canvas.width
      var h = canvas.height
      var cx = w / 2
      var cy = h / 2
      var r = 90

      var labels = [
        this.$t('report.listening'),
        this.$t('report.oral'),
        this.$t('report.reading'),
        this.$t('report.writing'),
        this.$t('report.radarSpeaking'),
        this.$t('report.radarGrammar')
      ]
      var scores = this.reportData.radarScores
      var values = [
        scores.listening / 100,
        scores.oral / 100,
        scores.reading / 100,
        scores.writing / 100,
        scores.speaking / 100,
        scores.grammar / 100
      ]
      var n = labels.length
      var angleStep = (Math.PI * 2) / n
      var startAngle = -Math.PI / 2

      // 画网格层
      ctx.strokeStyle = '#d0dff5'
      ctx.lineWidth = 1
      for (var level = 1; level <= 4; level++) {
        var lr = (r * level) / 4
        ctx.beginPath()
        for (var i = 0; i <= n; i++) {
          var angle = startAngle + angleStep * (i % n)
          var x = cx + lr * Math.cos(angle)
          var y = cy + lr * Math.sin(angle)
          if (i === 0) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        }
        ctx.closePath()
        ctx.stroke()
      }

      // 画轴线
      for (var j = 0; j < n; j++) {
        var axAngle = startAngle + angleStep * j
        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.lineTo(cx + r * Math.cos(axAngle), cy + r * Math.sin(axAngle))
        ctx.stroke()
      }

      // 画数据区域
      ctx.beginPath()
      for (var k = 0; k < n; k++) {
        var dAngle = startAngle + angleStep * k
        var dr = r * values[k]
        var dx = cx + dr * Math.cos(dAngle)
        var dy = cy + dr * Math.sin(dAngle)
        if (k === 0) ctx.moveTo(dx, dy)
        else ctx.lineTo(dx, dy)
      }
      ctx.closePath()
      ctx.fillStyle = 'rgba(96,165,250,0.25)'
      ctx.fill()
      ctx.strokeStyle = '#3B82F6'
      ctx.lineWidth = 2
      ctx.stroke()

      // 画数据点
      for (var m = 0; m < n; m++) {
        var pAngle = startAngle + angleStep * m
        var pr = r * values[m]
        var px = cx + pr * Math.cos(pAngle)
        var py = cy + pr * Math.sin(pAngle)
        ctx.beginPath()
        ctx.arc(px, py, 3, 0, Math.PI * 2)
        ctx.fillStyle = '#3B82F6'
        ctx.fill()
      }

      // 画标签
      ctx.fillStyle = '#555'
      ctx.font = '12px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      for (var l = 0; l < n; l++) {
        var lAngle = startAngle + angleStep * l
        var lx = cx + (r + 22) * Math.cos(lAngle)
        var ly = cy + (r + 22) * Math.sin(lAngle)
        ctx.fillText(labels[l], lx, ly)
      }
    }
  }
}
</script>

<style scoped>
.report-page {
  min-height: 100vh;
  background: #eef3fb;
  padding: 40px 20px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 顶部祝贺 */
.congrats-section {
  text-align: center;
  margin-bottom: 36px;
}
.congrats-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 20px;
}
.congrats-icon { flex-shrink: 0; }
.star-svg { width: 56px; height: 56px; }
.warning-svg { width: 56px; height: 56px; }
.congrats-icon.aborted {
  animation: pulse-warning 2s ease-in-out infinite;
}
@keyframes pulse-warning {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}
.congrats-text { text-align: left; }
.congrats-text h2 {
  font-size: 22px;
  font-weight: 700;
  color: #2563eb;
  margin: 0 0 4px;
}
.congrats-text p {
  font-size: 16px;
  color: #333;
  margin: 0;
  font-weight: 500;
}
.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}
.lang-btn {
  width: 36px; height: 36px; border-radius: 50%;
  border: 1px solid #2b7de9; background: #fff;
  color: #2b7de9; font-size: 12px; font-weight: 700;
  cursor: pointer; transition: all .2s;
  display: inline-flex; align-items: center; justify-content: center;
}
.lang-btn:hover { background: #2b7de9; color: #fff; }
.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #2b7de9;
  color: #fff;
  border: none;
  padding: 12px 48px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.download-btn:hover { background: #1a6ad4; }
.download-icon { width: 20px; height: 20px; }

.back-home-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: #2b7de9;
  border: 1px solid #2b7de9;
  padding: 12px 48px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.back-home-btn:hover { background: #eef4ff; }
.back-icon { width: 20px; height: 20px; }

/* 证书容器 */
.certificate-wrapper {
  width: 100%;
  max-width: 800px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  overflow: hidden;
}

.certificate {
  padding: 8px;
}

.cert-border {
  border: 3px solid #9fc5f8;
  border-radius: 4px;
  padding: 4px;
  position: relative;
}
.cert-border::before {
  content: '';
  position: absolute;
  inset: 4px;
  border: 1px solid #c8ddf6;
  border-radius: 2px;
  pointer-events: none;
}

.cert-inner {
  padding: 32px 48px 28px;
}

/* Logo */
.cert-logo {
  text-align: center;
  margin-bottom: 16px;
}
.cert-logo-img {
  height: 36px;
  width: auto;
  object-fit: contain;
}

/* 标题 */
.cert-title {
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 16px;
  color: #1a1a1a;
  margin: 0 0 4px;
}
.cert-title-en {
  text-align: center;
  font-size: 14px;
  color: #555;
  margin: 0 0 20px;
}
.cert-grade-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 8px 0 4px;
}
.grade-letter {
  display: inline-block;
  width: 60px;
  height: 60px;
  line-height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3B82F6 0%, #1d4ed8 100%);
  color: #fff;
  font-size: 32px;
  font-weight: 900;
  text-align: center;
  box-shadow: 0 2px 12px rgba(59,130,246,0.35);
  letter-spacing: 0;
}
/* CEFR level colors for grade-letter circle */
.grade-letter.grade-c2 { background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%); box-shadow: 0 2px 12px rgba(124,58,237,0.35); }
.grade-letter.grade-c1 { background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%); box-shadow: 0 2px 12px rgba(37,99,235,0.35); }
.grade-letter.grade-b2 { background: linear-gradient(135deg, #059669 0%, #065f46 100%); box-shadow: 0 2px 12px rgba(5,150,105,0.35); }
.grade-letter.grade-b1 { background: linear-gradient(135deg, #d97706 0%, #92400e 100%); box-shadow: 0 2px 12px rgba(217,119,6,0.35); }
.grade-letter.grade-a2 { background: linear-gradient(135deg, #ea580c 0%, #9a3412 100%); box-shadow: 0 2px 12px rgba(234,88,12,0.35); }
.grade-letter.grade-a1 { background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); box-shadow: 0 2px 12px rgba(220,38,38,0.35); }
.grade-letter.grade-default { background: linear-gradient(135deg, #3B82F6 0%, #1d4ed8 100%); box-shadow: 0 2px 12px rgba(59,130,246,0.35); }
.grade-label {
  font-size: 12px;
  color: #777;
  margin-top: 4px;
}
.td-grade {
  display: inline-block;
  background: #3B82F6;
  color: #fff;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  margin-top: 2px;
}
/* CEFR level colors for td-grade pill */
.td-grade.grade-c2 { background: #7c3aed; }
.td-grade.grade-c1 { background: #2563eb; }
.td-grade.grade-b2 { background: #059669; }
.td-grade.grade-b1 { background: #d97706; }
.td-grade.grade-a2 { background: #ea580c; }
.td-grade.grade-a1 { background: #dc2626; }
.td-grade.grade-default { background: #3B82F6; }
.cert-level {
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 2px;
}
.cert-level-en {
  text-align: center;
  font-size: 12px;
  color: #777;
  margin: 0 0 24px;
}

/* 个人信息 */
.cert-info {
  display: flex;
  gap: 24px;
  margin-bottom: 28px;
}
.cert-info-left { flex: 1; }
.info-row {
  display: flex;
  align-items: baseline;
  margin-bottom: 12px;
}
.info-row-half {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
}
.info-item {
  display: flex;
  align-items: baseline;
  flex: 1;
}
.info-label {
  font-size: 13px;
  color: #333;
  white-space: nowrap;
  margin-right: 4px;
  font-weight: 500;
}
.info-label::after {
  content: '';
}
.info-value {
  font-size: 13px;
  color: #333;
}
.info-line {
  flex: 1;
  border-bottom: 1px solid #999;
  min-width: 80px;
  padding-bottom: 2px;
  margin-left: 4px;
}
.info-line-short {
  display: inline-block;
  border-bottom: 1px solid #999;
  min-width: 40px;
  text-align: center;
  padding-bottom: 2px;
  margin: 0 2px;
}
.cert-photo {
  width: 100px;
  min-width: 100px;
  flex-shrink: 0;
  align-self: flex-start;
  line-height: 0;
  overflow: hidden;
  border: 1px solid #ddd;
  background: #f5f5f5;
}
.cert-photo img {
  display: block;
  width: 100px;
  height: 130px;
  object-fit: cover;
  object-position: center top;
}

/* 成绩表格 + 雷达图 */
.cert-scores {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 28px;
}
.score-table {
  border-collapse: collapse;
  font-size: 13px;
  flex-shrink: 0;
}
.score-table th,
.score-table td {
  border: 1px solid #999;
  padding: 8px 16px;
  text-align: center;
}
.score-table th {
  font-weight: 600;
  color: #333;
  background: #f8f9fc;
  font-size: 12px;
}
.score-table td {
  color: #333;
}
.td-en {
  font-size: 10px;
  color: #888;
}
.total-row td {
  font-weight: 700;
}

.radar-chart {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 底部 */
.cert-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 16px;
}
.cert-footer-left {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}
.cert-stamp-placeholder {
  width: 48px;
  height: 48px;
  background: #e5e7eb;
  border-radius: 4px;
}
.cert-sign {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sign-label {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}
.sign-line {
  display: block;
  width: 100px;
  border-bottom: 1px solid #999;
  height: 1px;
}
.cert-footer-right {
  text-align: right;
}
.cert-org {
  font-size: 13px;
  color: #333;
  font-weight: 600;
  margin-bottom: 2px;
}
.cert-org-en {
  font-size: 10px;
  color: #888;
  margin-bottom: 8px;
}
.cert-seal {
  width: 64px;
  height: 64px;
  border: 3px solid #e53e3e;
  border-radius: 50%;
  margin-left: auto;
  position: relative;
}
.cert-seal::after {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid #e53e3e;
  border-radius: 50%;
}

.cert-location {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  color: #333;
  font-weight: 500;
}
.cert-location-en {
  font-size: 11px;
  color: #888;
  font-weight: 400;
}

/* 打印 / 另存为 PDF：避免 flex、100vh、overflow 在打印引擎中错位 */
@media print {
  .report-page {
    display: block !important;
    align-items: stretch !important;
    min-height: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    background: #fff !important;
    padding: 0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .congrats-section {
    display: none !important;
  }
  .certificate-wrapper {
    box-shadow: none !important;
    border-radius: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    overflow: visible !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .certificate {
    padding: 0 !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .cert-inner {
    padding: 24px 28px 20px !important;
  }
  .cert-title {
    letter-spacing: 10px !important;
  }
  /* 必须横向：打印时视口常被算成窄屏，勿沿用移动端 column，否则头像会落到编号下方 */
  .cert-info {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: separate !important;
    border-spacing: 16px 0 !important;
    margin-bottom: 20px !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .cert-info-left {
    display: table-cell !important;
    vertical-align: top !important;
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    flex: none !important;
    padding-right: 4px !important;
    box-sizing: border-box !important;
  }
  .cert-photo {
    display: table-cell !important;
    vertical-align: top !important;
    width: 100px !important;
    min-width: 100px !important;
    max-width: 100px !important;
    flex: none !important;
    flex-shrink: 0 !important;
    align-self: flex-start !important;
    line-height: 0 !important;
    overflow: hidden !important;
    border: 1px solid #ccc !important;
    background: #f5f5f5 !important;
    box-sizing: border-box !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .cert-photo img {
    display: block !important;
    width: 100px !important;
    height: 130px !important;
    max-width: 100px !important;
    min-height: 130px !important;
    object-fit: cover !important;
    object-position: center top !important;
    border: none !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .cert-logo-img {
    max-width: 100% !important;
    height: auto !important;
  }
  /* 打印时强制横向双栏，避免换行、挤压或重叠 */
  .cert-scores {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: flex-start !important;
    justify-content: space-between !important;
    gap: 12px !important;
    width: 100% !important;
    margin-bottom: 20px !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .cert-scores .score-table {
    flex: 0 0 54% !important;
    max-width: 54% !important;
    min-width: 0 !important;
    width: 54% !important;
  }
  .cert-scores .radar-chart {
    flex: 0 0 42% !important;
    max-width: 42% !important;
    min-width: 0 !important;
    width: 42% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .cert-scores .radar-chart canvas {
    display: block !important;
    margin: 0 auto !important;
    /* 固定比例，避免 height:auto 在打印时把 canvas 压扁 */
    width: 210px !important;
    height: 210px !important;
    max-width: 100% !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .score-table th {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .grade-letter,
  .td-grade {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .cert-footer {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: flex-end !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .cert-border {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}

@media screen and (max-width: 768px) {
  .report-page { padding: 20px 12px 40px; }
  .cert-inner { padding: 20px 20px 16px; }
  .cert-title { font-size: 20px; letter-spacing: 8px; }
  .cert-info { flex-direction: column; }
  .cert-photo {
    width: 80px;
    min-width: 80px;
    align-self: center;
  }
  .cert-photo img {
    width: 80px;
    height: 104px;
  }
  .cert-scores { flex-direction: column; }
  .radar-chart { width: 100%; }
  .cert-footer { flex-direction: column; gap: 20px; align-items: flex-start; }
}

/* 题目详情 */

<style>
/* 打印 / PDF：页边距、取消 #app 整页高度，避免多出一页空白 */
@media print {
  @page {
    size: A4 portrait;
    margin: 12mm;
  }
  html,
  body {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  #app {
    min-height: 0 !important;
    height: auto !important;
  }
}
</style>
